/**
 * Horse Card Component
 * Displays a single horse with its details
 */
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { Horse, HorseSex } from "./types";
import HorseIcon from "./HorseIcon";

interface HorseCardProps {
  horse: Horse;
  ownerName?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  managerMode: number;
}

const SEX_LABELS: Record<HorseSex, string> = {
  mare: "Mare",
  gelding: "Gelding",
  stallion: "Stallion",
};

function getAgeLabel(birthdate?: string | null): string {
  if (!birthdate) return "Age: UNKNOWN";
  const birth = dayjs(birthdate);
  if (!birth.isValid()) return "Age: UNKNOWN";
  const years = dayjs().diff(birth, "year");
  return `${years} ${years === 1 ? "year" : "years"} old`;
}

function HorseCard({
  horse,
  ownerName,
  onEdit,
  onDelete,
  managerMode,
}: HorseCardProps) {
  const ageLabel = getAgeLabel(horse.birthdate);
  const ageUnknown = !horse.birthdate;

  return (
    <Card
      variant="outlined"
      sx={{
        opacity: horse.status === "unavailable" ? 0.6 : 1,
        transition: "all 0.2s",
        "&:hover": {
          boxShadow: 2,
        },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <HorseIcon color={horse.color} size={50} />
          
          <Box sx={{ flex: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" component="div">
                  {horse.name}
                </Typography>
                <Chip
                  size="small"
                  label={horse.status === "available" ? "Available" : "Unavailable"}
                  color={horse.status === "available" ? "success" : "default"}
                />
              </Stack>
              
              {(onEdit || onDelete) && (
                <Stack direction="row" spacing={0.5}>
                  {onEdit && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={onEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDelete && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={onDelete}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
              <Chip
                size="small"
                label={SEX_LABELS[horse.sex] || "Unknown"}
                color={horse.sex === "mare" ? "secondary" : "default"}
                variant="outlined"
              />

              <Chip
                size="small"
                label={ageLabel}
                color={ageUnknown ? "default" : "info"}
                variant="outlined"
              />
              
              <Chip
                size="small"
                label={`${horse.game_chukkers} chukker${horse.game_chukkers > 1 ? "s" : ""}/game`}
                color="primary"
                variant="outlined"
              />
              
              {/*<Chip
                size="small"
                label={
                  horse.owner_type === "club"
                    ? "Club Owned"
                    : ownerName || "Player Owned"
                }
                variant="outlined"
              />*/}
            </Stack>

            {horse.notes && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                {horse.notes}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default HorseCard;

